/**
 * Utilidades de bajo nivel del dataLayer (Guía de Medición v9). Push tipado y
 * normalización de valores. Todo string de valor debe pasar por `slugify` antes
 * del push (guía `02_Snippets&DataLayer.md` §3 y `08_Implementacion_QA.md`).
 */

import type { PageContext } from './types'

declare global {
  interface Window {
    /** Cola de GTM. Inicializada por el snippet de `<head>` (guía `03_GTM.md`). */
    dataLayer?: Record<string, unknown>[]
  }
}

/** `business_unit` es constante para todo el frontal (variables · contexto de página). */
export const BUSINESS_UNIT = 'entradas_frontal'

/**
 * Normalización canónica de la guía: minúsculas, sin tildes, espacios → `_`.
 * Mismo criterio que `SelectField.normalizeForSearch`, extendido con el
 * reemplazo de espacios que exige el dataLayer.
 */
export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
}

/**
 * Empuja un objeto al `window.dataLayer`. No-op en servidor (el tracking es
 * interactividad de cliente). Crea la cola si el snippet aún no la inicializó.
 */
export function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push(payload)
}

/**
 * Completa el contexto con los campos que solo existen en cliente/constantes:
 * `page_location` (URL real en el momento del push) y `business_unit`. Vive
 * aquí (no en `PageAnalyticsContext`) porque tanto ese provider como
 * `ChromeAnalyticsContext` lo necesitan — colocarlo en cualquiera de los dos
 * crearía un import circular entre ambos.
 */
export function withPageContext(
  context: PageContext,
  extra: Record<string, unknown>,
): Record<string, unknown> {
  return {
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    page_title: context.page_title,
    page_type: context.page_type,
    page_subtype: context.page_subtype,
    page_hierarchy: context.page_hierarchy,
    page_brand: context.page_brand,
    page_brand_type: context.page_brand_type,
    business_unit: BUSINESS_UNIT,
    ...extra,
  }
}

export {}
